import React, { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Seo from '../components/Seo';
import Faq from '../components/Faq';
import { Reveal, MaskLine, Eyebrow } from '../components/Reveal';
import { products, spmSpotlight, img, EMAIL, waLink } from '../data/content';
import { productSeo, slugToProductId, productSchema, faqSchema, breadcrumbSchema } from '../data/seo';

function Gallery({ p, seo }) {
  const [idx, setIdx] = useState(0);
  const imgs = [...new Set(p.images)];
  return (
    <div>
      <div className="clip-notch border border-steelline/80 bg-surface overflow-hidden">
        <img key={imgs[idx]} src={img(imgs[idx])} alt={`${seo.h1} by Tekwash Systems Pune — view ${idx + 1}`} className="w-full aspect-[4/3] object-cover" data-testid="product-detail-image" />
      </div>
      {imgs.length > 1 && (
        <div className="mt-3 flex gap-3" data-testid="product-detail-thumbs">
          {imgs.map((im, i) => (
            <button key={im} onClick={() => setIdx(i)} data-testid={`product-detail-thumb-${i}`} aria-label={`View image ${i + 1}`}
              className={`w-20 aspect-[4/3] overflow-hidden border transition-colors ${i === idx ? 'border-flame' : 'border-steelline hover:border-fog'}`}>
              <img src={img(im)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SpecTable() {
  return (
    <div className="mt-10 border border-steelline/60 divide-y divide-steelline/50 bg-surface/50" data-testid="product-detail-spec-table">
      {spmSpotlight.specs.map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 gap-4 px-6 py-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-fog pt-1">{k}</span>
          <span className="col-span-2 text-sm text-paper">{v}</span>
        </div>
      ))}
    </div>
  );
}

function Related({ current }) {
  const others = products.filter((x) => x.id !== current).slice(0, 3);
  return (
    <section className="py-20 bg-surface/40 border-t border-steelline/60" data-testid="product-detail-related">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Eyebrow className="mb-4">Related Machines</Eyebrow>
        <div className="grid md:grid-cols-3 gap-6">
          {others.map((o) => (
            <Link key={o.id} to={`/products/${productSeo[o.id].slug}`} data-testid={`related-${o.id}`} className="group block bg-ink border border-steelline/60 hover:border-flame/60 transition-colors">
              <img src={img(o.images[0])} alt={productSeo[o.id].h1} className="w-full aspect-[16/9] object-cover" loading="lazy" />
              <div className="p-5">
                <h3 className="font-display font-bold text-lg text-paper group-hover:text-flame transition-colors">{productSeo[o.id].h1}</h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-skyc">{o.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const id = slugToProductId[slug];
  if (!id) return <Navigate to="/products" replace />;
  const p = products.find((x) => x.id === id);
  const seo = productSeo[id];

  const schemas = [
    productSchema(p, seo),
    faqSchema(seo.faqs),
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Machines & Products', path: '/products' }, { name: seo.h1, path: `/products/${seo.slug}` }])
  ];

  return (
    <>
      <Seo title={seo.title} description={seo.description} keywords={seo.keywords} image={img(p.images[0])} type="product" schemas={schemas} />

      <section className="relative pt-36 pb-16 blueprint-grid overflow-hidden" data-testid="product-detail-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 to-ink" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8 relative z-10">
          <nav aria-label="Breadcrumb" className="mb-6 font-mono text-[11px] uppercase tracking-[0.2em] text-fog flex flex-wrap gap-2" data-testid="product-detail-breadcrumb">
            <Link to="/" className="hover:text-flame transition-colors">Home</Link><span aria-hidden="true">/</span>
            <Link to="/products" className="hover:text-flame transition-colors">Machines</Link><span aria-hidden="true">/</span>
            <span className="text-paper">{seo.h1}</span>
          </nav>
          <Eyebrow className="mb-5">{p.tagline}</Eyebrow>
          <h1 className="font-display font-extrabold text-paper leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="product-detail-h1">
            <MaskLine delay={0.1}>{seo.h1}</MaskLine>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-fog leading-relaxed">Manufacturer, supplier & exporter — Pune, Maharashtra, India.</p>
        </div>
      </section>

      <section className="pb-20" data-testid="product-detail-body">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-6"><Gallery p={p} seo={seo} /></Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            {seo.intro.map((para) => <p key={para.slice(0, 20)} className="text-base text-fog leading-relaxed mb-5">{para}</p>)}
            <h2 className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-flame">Key Features</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="product-detail-features">
              {p.bullets.map((b) => <li key={b} className="flex gap-3 text-sm text-paper items-start"><span className="text-flame mt-[3px]" aria-hidden="true">▸</span>{b}</li>)}
            </ul>
            <h2 className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-flame">Typical Applications</h2>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="product-detail-applications">
              {seo.applications.map((a) => <span key={a} className="font-mono text-[10px] uppercase tracking-[0.15em] text-fog border border-steelline px-2.5 py-1">{a}</span>)}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {p.chips.map((c) => <span key={c} className="font-mono text-[10px] uppercase tracking-[0.15em] text-skyc border border-azure/40 px-2.5 py-1">{c}</span>)}
            </div>
            {id === 'spm-compact' && <SpecTable />}
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={waLink(`Hello Tekwash Systems, I am interested in the ${seo.h1}.`)} target="_blank" rel="noopener noreferrer" data-testid="product-detail-whatsapp-cta" className="bg-flame text-white font-semibold px-6 py-3.5 clip-notch hover:bg-flamedim transition-colors">Request Quote on WhatsApp</a>
              <a href={`mailto:${EMAIL}?subject=${encodeURIComponent('Quote Request — ' + seo.h1)}`} data-testid="product-detail-email-cta" className="border border-steelline text-paper px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:border-flame hover:text-flame transition-colors flex items-center">Email For Specs</a>
            </div>
          </Reveal>
        </div>
      </section>

      <Faq faqs={seo.faqs} heading={`${seo.h1} — frequently asked questions`} testid="product-faq" />
      <Related current={id} />
    </>
  );
}
